import { useEffect, useState } from 'react'
import { AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { apiService, DoublonPossible, TypeFusion } from '@/services/api'

/* L'alerte de doublon à la saisie (28/10/2026).

   Arbitrage d'Arnaud : on avertit sans bloquer. Un contact dont l'adresse
   email existe déjà, une entreprise de même SIRET ou de même nom (aux accents,
   à la ponctuation et aux espaces près) font apparaître ce bandeau, avec de
   quoi ouvrir la fiche existante ; on peut quand même enregistrer (homonymes,
   deux établissements d'une même société). */

interface Props {
  type: TypeFusion
  email?: string
  nom?: string
  siret?: string
  /** La fiche qu'on modifie, à ne pas se signaler à elle-même. */
  sauf?: number
  onOuvrir: (id: number) => void
  /** En modification seulement : fusionner la fiche existante dans celle-ci. */
  onFusionner?: (id: number) => void
}

export default function AlerteDoublon({ type, email = '', nom = '', siret = '', sauf, onOuvrir, onFusionner }: Props) {
  const [trouves, setTrouves] = useState<DoublonPossible[]>([])

  useEffect(() => {
    const assez = type === 'contact' ? email.includes('@') : nom.trim().length >= 2 || siret.replace(/\D/g, '').length >= 9
    if (!assez) {
      setTrouves([])
      return
    }
    let abandon = false
    const minuterie = setTimeout(async () => {
      const r = type === 'contact'
        ? await apiService.getContactsMemeEmail(email.trim(), sauf)
        : await apiService.getEntreprisesSemblables(nom.trim(), siret.trim(), sauf)
      if (!abandon) setTrouves(r.success && r.data ? r.data : [])
    }, 400)
    return () => { abandon = true; clearTimeout(minuterie) }
  }, [type, email, nom, siret, sauf])

  if (!trouves.length) return null

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm space-y-2">
      <div className="flex items-center gap-2 font-semibold text-amber-800">
        <AlertTriangle className="w-4 h-4" />
        {trouves.length === 1
          ? (type === 'contact' ? 'Ce contact existe peut-être déjà' : 'Cette entreprise existe peut-être déjà')
          : (type === 'contact' ? 'Ce contact existe peut-être déjà plusieurs fois' : 'Cette entreprise existe peut-être déjà plusieurs fois')}
      </div>
      {trouves.map((t) => (
        <div key={t.id} className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <span className="font-medium">{t.nom}</span>
            <span className="text-amber-700"> ({t.raison})</span>
            {t.detail && <div className="text-xs text-gray-600 truncate">{t.detail}</div>}
          </div>
          <div className="flex gap-2 shrink-0">
            <Button type="button" size="sm" variant="outline" onClick={() => onOuvrir(t.id)}>Ouvrir</Button>
            {onFusionner && (
              <Button type="button" size="sm" variant="outline" onClick={() => onFusionner(t.id)}>Fusionner</Button>
            )}
          </div>
        </div>
      ))}
      <p className="text-xs text-amber-700">Vous pouvez tout de même enregistrer, s’il s’agit bien d’une autre fiche.</p>
    </div>
  )
}
